import type { RawReading } from '@/domain/types'

export type PresenceMetric = 'mesos'
export type PresenceBreakReason = 'source-hidden' | 'source-uncertain' | 'sample-gap' | 'source-changed' | 'missing-evidence'
export function samplingGapBudgetMs(intervalMs: number): number {
  return Math.max(2_000, 3 * (Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : 0) + 2_000)
}
interface Presence { startAt: number; lastAt: number; identity?: number }

/** Tracks new visibility observations separately from much slower numeric OCR. */
export class SourceContinuity {
  private chains = new Map<PresenceMetric, Presence>()
  private lastFrameAt = -Infinity
  private enabled = false

  reset(): void { this.chains.clear(); this.lastFrameAt = -Infinity; this.enabled = false }
  invalidate(metric: PresenceMetric): void { this.chains.delete(metric) }
  get hasEvidence(): boolean { return this.enabled }
  startAt(metric: PresenceMetric): number | null { return this.chains.get(metric)?.startAt ?? null }
  lastAt(metric: PresenceMetric): number | null { return this.chains.get(metric)?.lastAt ?? null }
  contains(metric: PresenceMetric, at: number): boolean {
    const chain = this.chains.get(metric)
    return !!chain && at >= chain.startAt && at <= chain.lastAt
  }

  observe(reading: RawReading): Partial<Record<PresenceMetric, PresenceBreakReason>> {
    const broken: Partial<Record<PresenceMetric, PresenceBreakReason>> = {}
    if (reading.capturedAt <= this.lastFrameAt) return broken
    this.lastFrameAt = reading.capturedAt
    const source = reading.sourceObservation
    if (!source) {
      if (this.enabled) for (const metric of ['mesos'] as const) broken[metric] = 'missing-evidence'
      this.enabled = false
      this.chains.clear()
      return broken
    }
    this.enabled = true
    const budget = samplingGapBudgetMs(source.samplingIntervalMs)
    for (const metric of ['mesos'] as const) {
      const previous = this.chains.get(metric)
      const at = reading.capturedAt
      const visible = source.mesosVisibility === 'visible'
      const identity = reading.mesosContinuityId
      let reason: PresenceBreakReason | undefined
      if (!visible) reason = source.mesosVisibility === 'uncertain' ? 'source-uncertain' : 'source-hidden'
      else if (!Number.isFinite(at) || at > reading.capturedAt || reading.capturedAt - at > budget) reason = 'sample-gap'
      else if (previous && identity !== previous.identity) reason = 'source-changed'
      else if (previous && (at - previous.lastAt > budget || reading.capturedAt - previous.lastAt > budget)) reason = 'sample-gap'
      if (reason) {
        if (previous) broken[metric] = reason
        this.chains.delete(metric)
      }
      if (!visible || !Number.isFinite(at) || at > reading.capturedAt || reading.capturedAt - at > budget) continue
      const chain = this.chains.get(metric)
      if (!chain) this.chains.set(metric, { startAt: at, lastAt: at, identity })
      else if (at > chain.lastAt) chain.lastAt = at
    }
    return broken
  }
}
